import React, { type PropsWithChildren, type CSSProperties, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { usePopper } from "react-popper";
import { type Placement } from "@popperjs/core";

import { SupBackdrop } from "../../../components/UI/Backdrop";
import { usePortalRoot } from "../../../hooks/usePortalRoot";
import { useOutsideClickHandler } from "../../../hooks/useOutsideClickHandler";

export interface PopoverProps extends PropsWithChildren {
  open?: boolean;
  className?: string;
  backdropClassName?: string;
  placement?: Placement;
  anchorEl?: Element | null;
  offset?: [number, number];
  portal?: boolean;
  backdrop?: boolean;
  onClickOutside?: () => void;
  style?: CSSProperties;
}

export function SupPopover({
  open = false,
  portal = false,
  backdrop = false,
  className,
  backdropClassName,
  anchorEl,
  placement = "bottom-start",
  offset = [0, 0],
  children,
  style,
  onClickOutside,
}: PopoverProps) {
  const [tooltipRef, setTooltipRef] = useState<HTMLElement | null>(null);
  const { styles } = usePopper(anchorEl, tooltipRef, { placement, modifiers: [{ name: "offset", options: { offset } }] });
  const root = usePortalRoot(portal);

  useOutsideClickHandler([tooltipRef, anchorEl], onClickOutside, { enabled: open });

  const content = open ? (
    <>
      <div ref={setTooltipRef} style={{ ...styles.popper, ...style }} className={twMerge("sup-popover p-6 bg-neutral-50 z-50", className)}>
        {children}
      </div>
      <SupBackdrop open={backdrop} portal={!portal} className={twMerge("bg-transparent", backdropClassName)} />
    </>
  ) : null;

  return root && portal ? createPortal(content, root) : content;
}
